import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BulbOutlined, BulbFilled, LogoutOutlined } from "@ant-design/icons";
import { useAuthStore } from "../../store/auth/auth.store";
import { useThemeMode } from "../../theme/ThemeProvider";

interface MenuItem {
  key: string;
  label: string;
  path?: string;
  children?: { key: string; label: string; path: string }[];
}

const MENU: MenuItem[] = [
  {
    key: "dashboard",
    label: "대시보드",
    path: "/dashboard",
  },
  {
    key: "solution",
    label: "솔루션",
    children: [
      { key: "solution-group", label: "솔루션그룹", path: "/solution-groups" },
      { key: "solution-item", label: "솔루션", path: "/solutions" },
    ],
  },
  {
    key: "indicator",
    label: "인디케이터",
    children: [
      { key: "indicator-group", label: "지표그룹", path: "/indicator-groups" },
      { key: "indicator-item", label: "인디케이터", path: "/indicators" },
    ],
  },
  {
    key: "question",
    label: "문항관리",
    children: [
      { key: "category", label: "카테고리", path: "/categories" },
      { key: "question-item", label: "문항", path: "/questions" },
    ],
  },
  {
    key: "risk",
    label: "위험규칙",
    children: [{ key: "risk-rule", label: "위험규칙", path: "/risk-rules" }],
  },
  {
    key: "procourse",
    label: "프로코스",
    children: [
      { key: "procourse-item", label: "프로코스설정", path: "/pro-courses" },
    ],
  },
  {
    key: "assess",
    label: "성향유형",
    children: [
      { key: "assess-type-group", label: "유형그룹", path: "/assess-type-groups" },
      { key: "assess-type", label: "성향유형", path: "/assess-types" },
      { key: "assess-report", label: "리포트 조회", path: "/assessment-reports" },
    ],
  },
  {
    key: "community",
    label: "커뮤니티",
    children: [
      { key: "community-report", label: "신고 관리", path: "/community/reports" },
      { key: "community-post", label: "게시글 관리", path: "/community/posts" },
      { key: "community-comment", label: "댓글 관리", path: "/community/comments" },
      { key: "community-customer", label: "고객 관리", path: "/community/customers" },
    ],
  },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();
  const { mode, toggle } = useThemeMode();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(MENU.map((m) => [m.key, true])),
  );

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="app-sidebar">
      <div
        onClick={() => navigate("/dashboard")}
        title="대시보드로 이동"
        style={{
          padding: "22px 20px 16px",
          borderBottom: "1px solid var(--app-border)",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          cursor: "pointer",
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: 0.3, color: "var(--app-text)" }}>
          knollo <span style={{ color: "var(--app-accent-text)" }}>console</span>
        </span>
        <span style={{ fontSize: 11, color: "var(--app-text-faint)" }}>관리자 콘솔</span>
      </div>

      <nav style={{ flex: 1, padding: "8px 0" }}>
        {MENU.map((group) => {
          const groupActive = !!group.path && location.pathname === group.path;
          return (
            <div key={group.key} style={{ marginBottom: 2 }}>
              <button
                className={`app-nav-header${groupActive ? " active" : ""}`}
                onClick={() =>
                  group.path ? navigate(group.path) : toggleGroup(group.key)
                }
              >
                <span>{group.label}</span>
                {group.children && (
                  <span style={{ fontSize: 11 }}>
                    {openGroups[group.key] ? "▾" : "▸"}
                  </span>
                )}
              </button>
              {openGroups[group.key] && group.children && (
                <div>
                  {group.children.map((item) => (
                    <button
                      key={item.key}
                      className={`app-nav-item${location.pathname === item.path ? " active" : ""}`}
                      onClick={() => navigate(item.path)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        <button className="app-sidebar-btn" onClick={toggle}>
          {mode === "dark" ? <BulbFilled /> : <BulbOutlined />}
          <span>{mode === "dark" ? "라이트 모드" : "다크 모드"}</span>
        </button>
        <button className="app-sidebar-btn" onClick={logout}>
          <LogoutOutlined />
          <span>로그아웃</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
